import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  Pause,
  Play,
  RefreshCw,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardHeader } from '@/components/ui/Card';
import { Select } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { api } from '@/lib/api';

interface LogEntry {
  id: string;
  svc: 'api' | 'worker' | string;
  ts: number | null;
  level: 'fatal' | 'error' | 'warn' | 'info' | 'debug' | 'trace' | null;
  msg: string | null;
  raw: Record<string, unknown> | null;
}

const LEVEL_COLOR: Record<string, string> = {
  fatal: 'bg-red-700 text-white',
  error: 'bg-red-100 text-red-800 border border-red-300',
  warn: 'bg-amber-100 text-amber-800 border border-amber-300',
  info: 'bg-blue-50 text-blue-800 border border-blue-200',
  debug: 'bg-gray-100 text-gray-700 border border-gray-200',
  trace: 'bg-gray-50 text-gray-500 border border-gray-200',
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200] as const;
const ALL_LEVELS = ['fatal', 'error', 'warn', 'info', 'debug', 'trace'] as const;
type Level = (typeof ALL_LEVELS)[number];

const TIME_RANGES = [
  { key: 'all', label: 'Tout', windowMs: null as number | null },
  { key: '5m', label: '5 min', windowMs: 5 * 60_000 },
  { key: '15m', label: '15 min', windowMs: 15 * 60_000 },
  { key: '1h', label: '1 h', windowMs: 60 * 60_000 },
  { key: '24h', label: '24 h', windowMs: 24 * 60 * 60_000 },
] as const;

export default function LogsPage() {
  const [svcFilter, setSvcFilter] = useState<'' | 'api' | 'worker'>('');
  // Multi-select levels — empty Set means "all levels".
  const [levelSet, setLevelSet] = useState<Set<Level>>(new Set());
  const [timeRangeKey, setTimeRangeKey] = useState<(typeof TIME_RANGES)[number]['key']>('all');
  const [search, setSearch] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [limit, setLimit] = useState(1000);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(50);

  const query = useQuery<LogEntry[]>({
    queryKey: ['logs', svcFilter, limit],
    queryFn: async () => {
      const params: Record<string, string | number> = { limit };
      if (svcFilter) params.svc = svcFilter;
      // We intentionally don't push level to the API anymore — every level
      // filter is applied client-side so checkbox combinations are cheap to
      // toggle without a refetch.
      const res = await api.get<{ entries: LogEntry[] }>('/v1/admin/logs', params);
      return res.entries ?? [];
    },
    refetchInterval: autoRefresh ? 5000 : false,
    placeholderData: (prev) => prev,
  });

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const range = TIME_RANGES.find((r) => r.key === timeRangeKey);
    const minTs = range?.windowMs ? Date.now() - range.windowMs : null;
    return (query.data ?? []).filter((e) => {
      if (levelSet.size > 0 && (!e.level || !levelSet.has(e.level as Level))) {
        return false;
      }
      if (minTs != null && (e.ts == null || e.ts < minTs)) return false;
      if (needle) {
        const haystack = `${e.msg ?? ''} ${e.raw ? JSON.stringify(e.raw) : ''}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [query.data, search, levelSet, timeRangeKey]);

  function toggleLevel(l: Level) {
    setLevelSet((prev) => {
      const next = new Set(prev);
      if (next.has(l)) next.delete(l);
      else next.add(l);
      return next;
    });
  }

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  // Reset to page 0 whenever filters change so we don't end up on a page
  // that no longer exists.
  useEffect(() => {
    setPage(0);
  }, [search, svcFilter, levelSet, timeRangeKey, pageSize]);
  // Clamp once the filtered set shrinks below the current page.
  const safePage = Math.min(page, totalPages - 1);
  const paged = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);

  function asPlainText(entries: LogEntry[]): string {
    return entries
      .map((e) => {
        const ts = e.ts ? new Date(e.ts).toISOString() : '?';
        const lvl = (e.level ?? '?').toUpperCase().padEnd(5);
        const svc = e.svc.padEnd(7);
        const ctx = e.raw
          ? Object.entries(e.raw)
              .filter(([k]) => !['msg', 'level', 'time', 'pid', 'hostname'].includes(k))
              .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
              .join(' ')
          : '';
        return `${ts} ${lvl} [${svc}] ${e.msg ?? ''}${ctx ? '  ' + ctx : ''}`;
      })
      .reverse() // oldest first when copying
      .join('\n');
  }

  function handleCopy() {
    const text = asPlainText(filtered);
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(`${filtered.length} lignes copiées`))
      .catch(() => toast.error('Impossible de copier'));
  }

  function handleExport() {
    const blob = new Blob([asPlainText(filtered)], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dispatcher-logs-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <PageHeader
        title="Logs"
        description="Tail temps réel API + worker (Redis stream, dernières 5 000 lignes)"
        action={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setAutoRefresh((v) => !v)}
              className="btn-ghost text-xs"
              title={autoRefresh ? 'Arrêter le rafraîchissement auto' : 'Reprendre auto-refresh'}
            >
              {autoRefresh ? <Pause size={12} /> : <Play size={12} />}{' '}
              {autoRefresh ? 'Auto' : 'Pause'}
            </button>
            <button
              type="button"
              onClick={() => query.refetch()}
              className="btn-ghost text-xs"
            >
              <RefreshCw size={12} /> Refresh
            </button>
            <button type="button" onClick={handleCopy} className="btn-ghost text-xs">
              <Copy size={12} /> Copier
            </button>
            <button type="button" onClick={handleExport} className="btn-ghost text-xs">
              <Download size={12} /> Export .txt
            </button>
          </div>
        }
      />

      <Card>
        <CardHeader
          title={`${filtered.length} ligne${filtered.length > 1 ? 's' : ''}${
            filtered.length > pageSize
              ? ` · page ${safePage + 1} / ${totalPages}`
              : ''
          }`}
        />
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-brand-border">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filtrer (msg ou contexte)…"
            className="input flex-1 min-w-[220px]"
          />
          <Select
            value={svcFilter}
            onChange={(e) => setSvcFilter(e.target.value as typeof svcFilter)}
            className="w-32"
          >
            <option value="">Tous services</option>
            <option value="api">api</option>
            <option value="worker">worker</option>
          </Select>
          <Select
            value={timeRangeKey}
            onChange={(e) =>
              setTimeRangeKey(e.target.value as (typeof TIME_RANGES)[number]['key'])
            }
            className="w-28"
            title="Fenêtre temporelle"
          >
            {TIME_RANGES.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label}
              </option>
            ))}
          </Select>
          <span className="text-xs text-gray-400 hidden md:inline">Charger</span>
          <Select
            value={String(limit)}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="w-24"
            title="Nombre de lignes chargées depuis le stream Redis"
          >
            <option value="100">100</option>
            <option value="300">300</option>
            <option value="500">500</option>
            <option value="1000">1000</option>
            <option value="2000">2000</option>
          </Select>
          <span className="text-xs text-gray-400 hidden md:inline">Par page</span>
          <Select
            value={String(pageSize)}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="w-20"
            title="Nombre de lignes affichées par page"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        </div>

        {/* Niveaux — multi-select via toggle chips */}
        <div className="flex flex-wrap items-center gap-1.5 px-4 py-2 border-b border-brand-border text-xs">
          <span className="text-gray-400 mr-1">Niveaux :</span>
          {ALL_LEVELS.map((lvl) => {
            const active = levelSet.has(lvl);
            return (
              <button
                key={lvl}
                type="button"
                onClick={() => toggleLevel(lvl)}
                className={`px-2 py-0.5 rounded font-mono uppercase transition-colors ${
                  active
                    ? LEVEL_COLOR[lvl] ?? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-50 text-gray-400 border border-gray-200 hover:bg-gray-100'
                }`}
                title={active ? `Cacher ${lvl}` : `Afficher ${lvl}`}
              >
                {lvl}
              </button>
            );
          })}
          {levelSet.size > 0 && (
            <button
              type="button"
              onClick={() => setLevelSet(new Set())}
              className="ml-2 text-gray-500 hover:text-gray-700 underline"
            >
              tous
            </button>
          )}
          <span className="ml-auto text-[10px] text-gray-400">
            {levelSet.size === 0 ? 'tous niveaux' : `${levelSet.size} niveau(x) sélectionné(s)`}
          </span>
        </div>

        <div className="max-h-[70vh] overflow-y-auto font-mono text-[11.5px]">
          {query.isLoading && !query.data ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-4 w-full" />
              ))}
            </div>
          ) : paged.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">
              Aucune ligne. Si Redis vient de redémarrer, attends qu'un nouveau log soit
              produit (un POST sur l'API ou un tick worker suffit).
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {paged.map((e) => (
                <LogRow key={e.id} entry={e} />
              ))}
            </div>
          )}
        </div>

        {filtered.length > pageSize && (
          <PaginationFooter
            page={safePage}
            totalPages={totalPages}
            onChange={setPage}
          />
        )}
      </Card>
    </div>
  );
}

interface PaginationFooterProps {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}

/**
 * Numbered pagination footer with prev/next + ellipsis. Renders 5–7
 * buttons regardless of total pages so the footer doesn't reflow as the
 * operator clicks through.
 */
function PaginationFooter({ page, totalPages, onChange }: PaginationFooterProps) {
  const last = totalPages - 1;
  const window = 1;
  const items: (number | 'ellipsis-l' | 'ellipsis-r')[] = [0];
  if (page - window > 1) items.push('ellipsis-l');
  for (let i = Math.max(1, page - window); i <= Math.min(last - 1, page + window); i++) {
    items.push(i);
  }
  if (page + window < last - 1) items.push('ellipsis-r');
  if (last > 0) items.push(last);

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-brand-border text-xs text-gray-500">
      <span>
        Page {page + 1} / {totalPages}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onChange(Math.max(0, page - 1))}
          disabled={page === 0}
          className="btn-ghost"
          aria-label="Page précédente"
        >
          <ChevronLeft size={14} /> Précédent
        </button>
        {items.map((it, idx) =>
          typeof it === 'number' ? (
            <button
              key={it}
              type="button"
              onClick={() => onChange(it)}
              className={`min-w-[28px] h-7 px-2 rounded text-xs ${
                it === page
                  ? 'bg-brand-primary text-white font-medium'
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              {it + 1}
            </button>
          ) : (
            <span key={`${it}-${idx}`} className="px-1 text-gray-400">
              …
            </span>
          ),
        )}
        <button
          type="button"
          onClick={() => onChange(Math.min(last, page + 1))}
          disabled={page >= last}
          className="btn-ghost"
          aria-label="Page suivante"
        >
          Suivant <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

function LogRow({ entry }: { entry: LogEntry }) {
  const [open, setOpen] = useState(false);
  const ts = entry.ts ? new Date(entry.ts) : null;
  const tsStr = ts
    ? `${ts.toLocaleTimeString('fr-FR', { hour12: false })}.${String(ts.getMilliseconds()).padStart(3, '0')}`
    : '?';
  const ctx = entry.raw
    ? Object.fromEntries(
        Object.entries(entry.raw).filter(
          ([k]) => !['msg', 'level', 'time', 'pid', 'hostname', 'v', 'name'].includes(k),
        ),
      )
    : null;
  const hasCtx = ctx && Object.keys(ctx).length > 0;
  return (
    <div className="px-4 py-1.5 hover:bg-gray-50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-start gap-2 text-left w-full"
      >
        <span className="text-gray-400 shrink-0">{tsStr}</span>
        <span
          className={`shrink-0 px-1.5 rounded text-[10px] font-bold ${
            LEVEL_COLOR[entry.level ?? 'info'] ?? LEVEL_COLOR.info
          }`}
        >
          {(entry.level ?? '???').toUpperCase()}
        </span>
        <span
          className={`shrink-0 px-1.5 rounded text-[10px] ${
            entry.svc === 'worker'
              ? 'bg-purple-50 text-purple-800 border border-purple-200'
              : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
          }`}
        >
          {entry.svc}
        </span>
        <span className="text-brand-dark break-all">{entry.msg ?? '(no msg)'}</span>
      </button>
      {open && hasCtx && (
        <pre className="mt-1 ml-12 text-[11px] bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
          {JSON.stringify(ctx, null, 2)}
        </pre>
      )}
    </div>
  );
}
